import { Fragment, useState, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XIcon } from '@heroicons/react/outline'
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import InputMask from 'react-input-mask';
import NeatcarButton from "./NeatcarButton";
import qr from '../assets/images/qr.png';
import downloadPlayStoreFr from '../assets/images/google-play-neatcar.png';
import downloadAppStoreFr from '../assets/images/app-store-neatcar.png';
import downloadPlayStoreEn from './../assets/images/google-play-neatcar-en.png';
import downloadAppStoreEn from './../assets/images/app-store-neatcar-en.png';
import sampleApp from './../assets/images/sample-app.png';

const DownloadAppModal = ({open, onClose, buy}) => {
  const { i18n, t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { register, handleSubmit } = useForm();
  
  // Touch swipe state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);

  // Minimum swipe distance (in px) to trigger close
  const minSwipeDistance = 50;

  // Reset drag state when modal closes
  useEffect(() => {
    if (!open) {
      setIsDragging(false);
      setDragOffset(0);
      setTouchStart(null);
      setTouchEnd(null);
    }
  }, [open]);

  // Wrapper for onClose that resets drag state first
  const handleClose = () => {
    setIsDragging(false);
    setDragOffset(0);
    setTouchStart(null);
    setTouchEnd(null);
    onClose();
  };

  const onSubmit = (data) => {
    console.log(data)
    setLoading(true);
    // DO SOMETHING
    handleClose();
  }

  // Handle touch start
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientY);
    setIsDragging(true);
  };

  // Handle touch move
  const onTouchMove = (e) => {
    if (!touchStart) return;
    
    const currentTouch = e.targetTouches[0].clientY;
    const distance = currentTouch - touchStart;
    
    // Only allow dragging down, not up
    if (distance > 0) {
      setDragOffset(distance);
      setTouchEnd(currentTouch);
    }
  };

  // Handle touch end
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setIsDragging(false);
      setDragOffset(0);
      return;
    }
    
    const distance = touchEnd - touchStart;
    const isDownSwipe = distance > minSwipeDistance;
    
    if (isDownSwipe) {
      handleClose();
    }
    
    // Reset state
    setIsDragging(false);
    setDragOffset(0);
    setTouchStart(null);
    setTouchEnd(null);
  };

  const renderContent = () => {
    if (buy) {
      return (
        <div className="flex flex-col mt-8 items-center gap-y-6">
          <div className="flex items-center">
            <img src={sampleApp} alt={'Neatcar app'} className="max-w-xs sm:max-w-sm" />
          </div>
          <div className="flex flex-col gap-y-6 w-full">
            <div className="flex flex-col sm:flex-row gap-x-8 gap-y-6">
              <div>
                <h2 className="font-bold text-base sm:text-lg">{t('choose')}</h2>
                <p className="text-sm sm:text-base text-neatcarGray">{t('chooseDesc')}</p>
              </div>
              <div>
                <h2 className="font-bold text-base sm:text-lg">{t('pay')}</h2>
                <p className="text-sm sm:text-base text-neatcarGray">{t('payDesc')}</p>
              </div>
            </div>
            <div className="flex flex-col items-center sm:items-start gap-y-3">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-x-6 gap-y-3">
                <div className="hidden sm:block flex-shrink-0">
                  <img src={qr} alt={'qr code'} className="w-32" />
                </div>
                <div className="flex flex-col items-center sm:items-start gap-y-3">
                  <h1 className="text-xl sm:text-2xl md:text-3xl text-neatcar font-bold whitespace-nowrap">{t('downloadApp')}</h1>
                  <div className="flex flex-row gap-x-4">
                    <a href={t('playStoreLink')} target={'_blank'} rel="noreferrer">
                      <img src={i18n.language === 'fr' ? downloadPlayStoreFr : downloadPlayStoreEn} width={'120'} alt={'Download on Google Play Store'} />
                    </a>
                    <a href={t('appStoreLink')} target={'_blank'} rel="noreferrer">
                      <img src={i18n.language === 'fr' ? downloadAppStoreFr : downloadAppStoreEn} width={'120'} alt={'Download on Apple App Store'} />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="flex flex-row gap-x-8 mt-8">
          <a href={t('playStoreLink')} target={'_blank'} rel="noreferrer">
            <img src={i18n.language === 'fr' ? downloadPlayStoreFr : downloadPlayStoreEn} width={'120'} alt={'Download on Google Play Store'} />
          </a>
          <a href={t('appStoreLink')} target={'_blank'} rel="noreferrer">
            <img src={i18n.language === 'fr' ? downloadAppStoreFr : downloadAppStoreEn} width={'120'} alt={'Download on Apple App Store'} />
          </a>
        </div>
      );
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-30" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <form className="flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0" onSubmit={handleSubmit(onSubmit)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel 
                className="relative bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-3xl sm:w-full sm:p-6"
                style={dragOffset > 0 ? {
                  transform: `translateY(${dragOffset}px)`,
                  transition: isDragging ? 'none' : 'transform 0.3s ease-out'
                } : undefined}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                {/* Drag handle for mobile */}
                <div className="flex justify-center sm:hidden mb-4">
                  <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
                </div>
                
                <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={() => handleClose()}
                  >
                    <span className="sr-only">Close</span>
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                
                {/* Close button for mobile */}
                <div className="sm:hidden absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={() => handleClose()}
                  >
                    <span className="sr-only">Close</span>
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="flex flex-col justify-center items-center">
                  {!buy && (<div>
                    <img src={qr} alt={'qr code'}/>
                  </div>)}
                  {renderContent()}
                  <div className="flex items-center self-stretch my-8">
                    <hr className="flex-1" />
                    <span className="px-4 text-gray-400 text-sm">OR</span>
                    <hr className="flex-1" />
                  </div>
                  <span className="self-start text-xs sm:text-sm text-neatcarGray mb-2">{t('sendToPhoneDesc')}</span>
                  <div className="flex self-stretch flex-col sm:flex-row gap-4 sm:gap-x-8">
                    <InputMask
                      {...register("phoneNumber", { required: { value: true, message: t('phoneRequired') }, pattern: /^\(\+1\) \([0-9]{3}\) [0-9]{3}-[0-9]{4}$/ })}
                      mask="(+1) (999) 999-9999"
                      maskChar="_"
                      alwaysShowMask={true}
                      type="text"
                      name="phoneNumber"
                      id="phoneNumber"
                      className="flex-1 px-4 focus:ring-neatcar focus:border-neatcar shadow-sm border block sm:text-sm rounded-md"
                    />
                    <NeatcarButton type={'submit'} loading={loading} title={t('sendToPhone')} className="flex-shrink-0 sm:w-auto" />
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </form>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default DownloadAppModal;
